import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, Eye, Database, Plus, ChevronLeft, ChevronRight, Maximize2, Minimize2, Search, X } from 'lucide-react'
import { templateApi, dataSourceApi, schemaApi } from '@/api/services'
import { useToast } from '@/context/ToastContext'
import { Button } from '@/components/ui/Button'
import { Badge, Spinner } from '@/components/ui/misc'
import { SchemaExplorer } from '@/components/designer/SchemaExplorer'
import { SqlEditor } from '@/components/designer/SqlEditor'
import { LiquidEditor, type LiquidEditorHandle } from '@/components/designer/LiquidEditor'
import { ExcelLayoutEditor } from '@/components/designer/ExcelLayoutEditor'
import { cn } from '@/lib/utils'
import {
  saveTemplateDraft,
  loadTemplateDraft,
  clearTemplateDraft,
  templateDraftMatchesServer,
  type TemplateDraftEnvelope,
} from '@/lib/templateDraft'
import { MAX_TEMPLATE_TAG_LENGTH, tryAddTagFromInput } from '@/lib/templateTags'
import { TemplateDraftConflictDialog } from '@/components/designer/TemplateDraftConflictDialog'
import { SnippetPicker, SNIPPET_DRAG_TYPE } from '@/components/SnippetPicker'
import type { DataSource, Template, ExcelConfig, ExcelCellStyle } from '@/types'
import { DEFAULT_EXCEL_STYLE_DEFAULTS } from '@/types'
import { formatApiError, toastApiError } from '@/lib/apiError'

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

type LeftTab  = 'datasources' | 'sql'
type RightTab = 'liquid' | 'excel'

const DS_DRAG_TYPE = 'text/x-datasource'

const DEFAULT_EXCEL_CONFIG = (): ExcelConfig => ({
  sheets: [],
  includeSummarySheet: false,
  summarySheetName: 'Summary',
  defaultFontName: DEFAULT_EXCEL_STYLE_DEFAULTS.defaultFontName,
  defaultFontSize: DEFAULT_EXCEL_STYLE_DEFAULTS.defaultFontSize,
  headerStyle: {
    fontName: '',
    fontSize: undefined,
    fontColor: DEFAULT_EXCEL_STYLE_DEFAULTS.headerFontColor,
    backgroundColor: DEFAULT_EXCEL_STYLE_DEFAULTS.headerBackgroundColor,
  },
  bodyStyle: {
    fontName: '',
    fontSize: undefined,
    fontColor: DEFAULT_EXCEL_STYLE_DEFAULTS.bodyFontColor,
    backgroundColor: '',
  },
  alternatingRows: DEFAULT_EXCEL_STYLE_DEFAULTS.alternatingRows,
  alternatingBackgroundColor: DEFAULT_EXCEL_STYLE_DEFAULTS.alternatingBackgroundColor,
})

function normalizeCellStyle(raw: unknown): ExcelCellStyle {
  const style = (raw ?? {}) as Record<string, unknown>
  return {
    fontName: String(style.fontName ?? style.FontName ?? ''),
    fontSize: (style.fontSize ?? style.FontSize) as number | undefined,
    fontColor: String(style.fontColor ?? style.FontColor ?? ''),
    backgroundColor: String(style.backgroundColor ?? style.BackgroundColor ?? ''),
  }
}

function normalizeExcelConfig(raw: unknown): ExcelConfig {
  const cfg = raw as Record<string, unknown>
  const sheets = Array.isArray(cfg.sheets) ? cfg.sheets : []
  const defaults = DEFAULT_EXCEL_CONFIG()
  return {
    includeSummarySheet: Boolean(cfg.includeSummarySheet ?? cfg.IncludeSummarySheet),
    summarySheetName: String(cfg.summarySheetName ?? cfg.SummarySheetName ?? 'Summary'),
    defaultFontName: String(cfg.defaultFontName ?? cfg.DefaultFontName ?? defaults.defaultFontName),
    defaultFontSize: Number(cfg.defaultFontSize ?? cfg.DefaultFontSize ?? defaults.defaultFontSize),
    headerStyle: {
      ...defaults.headerStyle,
      ...normalizeCellStyle(cfg.headerStyle ?? cfg.HeaderStyle),
    },
    bodyStyle: {
      ...defaults.bodyStyle,
      ...normalizeCellStyle(cfg.bodyStyle ?? cfg.BodyStyle),
    },
    alternatingRows: Boolean(cfg.alternatingRows ?? cfg.AlternatingRows ?? defaults.alternatingRows),
    alternatingBackgroundColor: String(
      cfg.alternatingBackgroundColor ?? cfg.AlternatingBackgroundColor ?? defaults.alternatingBackgroundColor
    ),
    sheets: sheets.map(s => {
      const sheet = s as Record<string, unknown>
      const columns = Array.isArray(sheet.columns) ? sheet.columns : []
      return {
        dataSourceKey: String(sheet.dataSourceKey ?? sheet.DataSourceKey ?? ''),
        sheetName: String(sheet.sheetName ?? sheet.SheetName ?? ''),
        freezeHeader: Boolean(sheet.freezeHeader ?? sheet.FreezeHeader ?? true),
        autoFilter: Boolean(sheet.autoFilter ?? sheet.AutoFilter ?? true),
        groupByField: (sheet.groupByField ?? sheet.GroupByField) as string | undefined,
        showSubtotals: Boolean(sheet.showSubtotals ?? sheet.ShowSubtotals),
        columns: columns.map(c => {
          const col = c as Record<string, unknown>
          return {
            field: String(col.field ?? col.Field ?? ''),
            header: String(col.header ?? col.Header ?? ''),
            width: (col.width ?? col.Width) as number | undefined,
            format: (col.format ?? col.Format) as string | undefined,
            hidden: Boolean(col.hidden ?? col.Hidden),
          }
        }),
      }
    }),
  }
}

export default function DesignerPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [template, setTemplate] = useState<Partial<Template>>({
    name: '', code: '', htmlContent: DEFAULT_HTML, requiredDataSourceKeys: [], tags: [], excelConfigJson: null,
  })
  const [tagInput, setTagInput] = useState('')
  const [allSources, setAllSources]     = useState<DataSource[]>([])
  const [dsTotalCount, setDsTotalCount] = useState(0)
  const [dsTotalPages, setDsTotalPages] = useState(1)
  const [dsPage, setDsPage]             = useState(1)
  const [dsSearch, setDsSearch]         = useState('')
  const [dsDebouncedSearch, setDsDebouncedSearch] = useState('')
  const [dsLoading, setDsLoading]       = useState(false)
  const dsSearchTimer = useRef<ReturnType<typeof setTimeout>>()
  const [leftTab, setLeftTab]           = useState<LeftTab>('datasources')
  const [rightTab, setRightTab]         = useState<RightTab>('liquid')
  const [editingDs, setEditingDs]       = useState<Partial<DataSource> | null>(null)
  const [saving, setSaving]             = useState(false)
  const [previewing, setPreviewing]     = useState(false)
  const [loadingTemplate, setLoadingTemplate] = useState(!!id)
  const [draftConflict, setDraftConflict] = useState<{
    server: Template
    envelope: TemplateDraftEnvelope
  } | null>(null)
  const [editorExpanded, setEditorExpanded]   = useState(false)
  const [sourcesPanelExpanded, setSourcesPanelExpanded] = useState(false)
  const [isDragOverEditor, setIsDragOverEditor] = useState(false)
  const [previewCols, setPreviewCols]   = useState<Record<string, string[]>>({})

  const previewRef    = useRef<HTMLIFrameElement>(null)
  const previewTimer  = useRef<ReturnType<typeof setTimeout>>()
  const liquidEditorRef = useRef<LiquidEditorHandle>(null)
  const draftTimer = useRef<ReturnType<typeof setTimeout>>()
  const autosaveTimer = useRef<ReturnType<typeof setTimeout>>()
  const autosaveReady = useRef(false)

  // Debounce datasource search
  useEffect(() => {
    clearTimeout(dsSearchTimer.current)
    dsSearchTimer.current = setTimeout(() => {
      setDsDebouncedSearch(dsSearch)
      setDsPage(1)
    }, 350)
    return () => clearTimeout(dsSearchTimer.current)
  }, [dsSearch])

  const loadSources = useCallback(async () => {
    setDsLoading(true)
    try {
      const res = await dataSourceApi.getAll({
        filter: dsDebouncedSearch || undefined,
        pageNumber: dsPage,
        pageSize: 10,
      })
      setAllSources(res.items)
      setDsTotalCount(res.totalCount)
      setDsTotalPages(res.totalPages)
    } catch { /* non-fatal in designer */ }
    finally { setDsLoading(false) }
  }, [dsDebouncedSearch, dsPage])

  useEffect(() => {
    setDraftConflict(null)

    if (!id) {
      setLoadingTemplate(false)
      const d = loadTemplateDraft(undefined)
      if (d?.template) {
        setTemplate(prev => ({ ...prev, ...d.template }))
      }
      return
    }

    setLoadingTemplate(true)
    templateApi
      .getById(id)
      .then(t => {
        const d = loadTemplateDraft(id)
        const serverTime = t.updatedAt ? new Date(t.updatedAt).getTime() : 0
        const draftNewer = !!(d && d.savedAt > serverTime)

        if (!draftNewer) {
          setTemplate(t)
          return
        }

        if (templateDraftMatchesServer(d!.template, t)) {
          clearTemplateDraft(id)
          setTemplate(t)
          return
        }

        setDraftConflict({ server: t, envelope: d! })
      })
      .catch((e: unknown) => toastApiError(toast, e, 'Failed to load template'))
      .finally(() => setLoadingTemplate(false))
  }, [id])

  useEffect(() => {
    if (draftConflict) return
    clearTimeout(draftTimer.current)
    draftTimer.current = setTimeout(() => {
      saveTemplateDraft(id, template)
    }, 500)
    return () => clearTimeout(draftTimer.current)
  }, [template, id, draftConflict])

  useEffect(() => {
    if (!id) {
      autosaveReady.current = false
      return
    }
    autosaveReady.current = false
    const t = setTimeout(() => {
      autosaveReady.current = true
    }, 2500)
    return () => clearTimeout(t)
  }, [id])

  useEffect(() => {
    if (!id || !autosaveReady.current || draftConflict) return
    if (!template.name || !template.code || !template.htmlContent) return

    clearTimeout(autosaveTimer.current)
    autosaveTimer.current = setTimeout(async () => {
      try {
        await templateApi.update(id, {
          name: template.name!,
          code: template.code!.toUpperCase(),
          htmlContent: template.htmlContent!,
          requiredDataSourceKeys: template.requiredDataSourceKeys ?? [],
          tags: template.tags ?? [],
          excelConfigJson: template.excelConfigJson ?? null,
        })
      } catch {
        // non-fatal background save
      }
    }, 60000)

    return () => clearTimeout(autosaveTimer.current)
  }, [template, id, draftConflict])

  useEffect(() => { loadSources() }, [loadSources])

  const previewColsFetchedRef = useRef<Set<string>>(new Set())

  // When active sources change, fetch their column names for the Excel layout editor
  useEffect(() => {
    const excelKeys = (() => {
      try {
        return template.excelConfigJson
          ? normalizeExcelConfig(JSON.parse(template.excelConfigJson)).sheets.map(s => s.dataSourceKey).filter(Boolean)
          : []
      } catch {
        return []
      }
    })()
    const keys = [...new Set([...(template.requiredDataSourceKeys ?? []), ...excelKeys])]
    if (keys.length === 0) return

    for (const k of [...previewColsFetchedRef.current]) {
      if (!keys.includes(k)) previewColsFetchedRef.current.delete(k)
    }

    let cancelled = false

    keys.forEach(async key => {
      if (previewColsFetchedRef.current.has(key)) return

      let ds = allSources.find(s => s.key === key)
      if (!ds) {
        try {
          const res = await dataSourceApi.getAll({ filter: key, pageSize: 50 })
          ds = res.items.find(s => s.key === key)
        } catch { return }
      }
      if (!ds || cancelled) return

      try {
        const res = await schemaApi.preview(`SELECT * FROM (${ds.sqlQuery}) _q LIMIT 1`)
        if (cancelled) return
        const cols = res.rows[0] ? Object.keys(res.rows[0]) : []
        previewColsFetchedRef.current.add(key)
        setPreviewCols(p => (key in p ? p : { ...p, [key]: cols }))
      } catch { /* non-fatal — allow retry when deps change */ }
    })

    return () => { cancelled = true }
  }, [template.requiredDataSourceKeys, template.excelConfigJson, allSources])

  const schedulePreview = useCallback(() => {
    clearTimeout(previewTimer.current)
    previewTimer.current = setTimeout(runPreview, 700)
  }, [template])

  async function runPreview() {
    if (!template.htmlContent) return
    setPreviewing(true)
    try {
      const html = await templateApi.designerPreview({
        htmlContent: template.htmlContent,
        requiredDataSourceKeys: template.requiredDataSourceKeys ?? [],
      })
      if (previewRef.current) previewRef.current.srcdoc = html
    } catch (e: unknown) {
      if (previewRef.current) {
        const msg = escapeHtml(formatApiError(e, 'Preview error'))
        previewRef.current.srcdoc = `<pre style="color:red;padding:16px;white-space:pre-wrap">${msg}</pre>`
      }
    } finally { setPreviewing(false) }
  }

  async function save() {
    if (!template.name) return toast('Template name is required', 'error')
    if (!template.code) return toast('Template code is required', 'error')
    if (template.code.length > 8) return toast('Code must be 8 characters max', 'error')
    setSaving(true)
    try {
      const payload = {
        name: template.name!,
        code: template.code!.toUpperCase(),
        htmlContent: template.htmlContent!,
        requiredDataSourceKeys: template.requiredDataSourceKeys!,
        tags: template.tags ?? [],
        excelConfigJson: template.excelConfigJson ?? null,
      }
      if (id) {
        await templateApi.update(id, payload)
        clearTemplateDraft(id)
        toast('Template saved')
      } else {
        const t = await templateApi.create(payload)
        clearTemplateDraft(undefined)
        navigate(`/designer/${t.id}`, { replace: true })
        toast('Template created')
      }
    } catch (e: unknown) {
      toastApiError(toast, e, 'Save failed')
    }
    finally { setSaving(false) }
  }

  function insertColumn(table: string, col: string) {
    const text = `${table}."${col}"`
    navigator.clipboard.writeText(text)
    setLeftTab('sql')
    toast(`Copied ${text} — paste into SQL editor`, 'info')
  }

  function toggleSource(key: string) {
    setTemplate(p => {
      const keys = p.requiredDataSourceKeys ?? []
      return { ...p, requiredDataSourceKeys: keys.includes(key) ? keys.filter(k => k !== key) : [...keys, key] }
    })
  }

  function handleEditorDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragOverEditor(false)
    const snippetContent = e.dataTransfer.getData(SNIPPET_DRAG_TYPE)
    if (snippetContent) {
      liquidEditorRef.current?.insertText(snippetContent)
      return
    }
    const payload = e.dataTransfer.getData(DS_DRAG_TYPE)
    if (!payload) return
    const { key, isCollection } = JSON.parse(payload) as { key: string; isCollection: boolean }
    const snippet = isCollection
      ? `{% for r in collection.${key} %}\n  {{ r. }}\n{% endfor %}`
      : `{{ collection.${key}. }}`
    liquidEditorRef.current?.insertText(snippet)
  }

  // ExcelConfig helpers
  const excelConfig: ExcelConfig = (() => {
    try {
      return template.excelConfigJson
        ? normalizeExcelConfig(JSON.parse(template.excelConfigJson))
        : DEFAULT_EXCEL_CONFIG()
    }
    catch { return DEFAULT_EXCEL_CONFIG() }
  })()

  function setExcelConfig(cfg: ExcelConfig) {
    setTemplate(p => ({ ...p, excelConfigJson: JSON.stringify(cfg) }))
  }

  const activeSources = allSources.filter(ds =>
    (template.requiredDataSourceKeys ?? []).includes(ds.key)
  )

  function resolveDraftConflict(choice: 'draft' | 'server' | 'back') {
    if (!draftConflict) return
    const { server, envelope } = draftConflict
    if (choice === 'draft') {
      setTemplate({ ...server, ...envelope.template, id: server.id })
    } else if (choice === 'server') {
      setTemplate(server)
      if (id) clearTemplateDraft(id)
    } else {
      navigate('/templates')
    }
    setDraftConflict(null)
  }

  if (loadingTemplate) return <div className="flex h-full items-center justify-center"><Spinner className="h-6 w-6" /></div>

  if (draftConflict) {
    return (
      <TemplateDraftConflictDialog
        server={draftConflict.server}
        envelope={draftConflict.envelope}
        onUseDraft={() => resolveDraftConflict('draft')}
        onUseServer={() => resolveDraftConflict('server')}
        onBackToTemplates={() => resolveDraftConflict('back')}
      />
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-2.5 flex-shrink-0">
        <button onClick={() => navigate('/templates')} className="text-muted hover:text-ink transition-colors">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <input
          className="flex-1 bg-transparent text-sm font-bold text-ink placeholder:text-muted focus:outline-none max-w-xs"
          placeholder="Template name…"
          value={template.name ?? ''}
          onChange={e => setTemplate(p => ({ ...p, name: e.target.value }))}
        />
        <div className="flex items-center gap-1.5">
          <input
            className="w-24 rounded border border-border2 bg-bg px-2 py-1 text-xs font-mono font-bold text-yellow uppercase tracking-widest focus:border-yellow focus:outline-none"
            placeholder="CODE"
            maxLength={8}
            value={template.code ?? ''}
            onChange={e => setTemplate(p => ({ ...p, code: e.target.value.toUpperCase() }))}
          />
          <span className="text-[10px] text-muted font-mono">{(template.code ?? '').length}/8</span>
        </div>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={runPreview} disabled={previewing || rightTab === 'excel'}>
          {previewing ? <Spinner className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
          Preview
        </Button>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : <><Save className="h-3 w-3" /> Save</>}
        </Button>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-bg/30 px-4 py-2 flex-shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted mr-0.5">Tags</span>
        {(template.tags ?? []).map(tag => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded border border-accent/30 bg-accent/5 px-2 py-0.5 text-[10px] font-mono text-ink"
          >
            {tag}
            <button
              type="button"
              className="text-muted hover:text-red transition-colors"
              onClick={() =>
                setTemplate(p => ({ ...p, tags: (p.tags ?? []).filter(x => x !== tag) }))
              }
              aria-label={`Remove tag ${tag}`}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
        <input
          className="min-w-[100px] flex-1 max-w-sm rounded border border-border2 bg-bg px-2 py-1 text-[11px] font-mono text-ink placeholder:text-muted focus:border-accent focus:outline-none"
          placeholder="Add (Enter) — max 50 chars"
          maxLength={MAX_TEMPLATE_TAG_LENGTH}
          value={tagInput}
          onChange={e => setTagInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              const parts = tagInput.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean)
              let next = template.tags ?? []
              for (const part of parts) {
                const { tags: t, error } = tryAddTagFromInput(part, next)
                if (error) {
                  toast(error, 'error')
                  return
                }
                next = t
              }
              setTemplate(p => ({ ...p, tags: next }))
              setTagInput('')
            } else if (e.key === 'Backspace' && !tagInput && (template.tags?.length ?? 0) > 0) {
              setTemplate(p => ({ ...p, tags: (p.tags ?? []).slice(0, -1) }))
            }
          }}
        />
        <span className="text-[10px] text-muted font-mono tabular-nums">
          {tagInput.length}/{MAX_TEMPLATE_TAG_LENGTH}
        </span>
      </div>

      {/* 3-column workspace */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Schema explorer */}
        {!editorExpanded && !sourcesPanelExpanded && (
          <div className="w-48 border-r border-border bg-surface flex-shrink-0 overflow-hidden flex flex-col">
            <SchemaExplorer onInsertColumn={insertColumn} />
          </div>
        )}

        {/* Middle: Data sources + SQL */}
        {!editorExpanded && (
          <div
            className={cn(
              'border-r border-border flex flex-col overflow-hidden',
              sourcesPanelExpanded ? 'min-w-0 flex-1' : 'w-80 flex-shrink-0'
            )}
          >
            <div className="flex border-b border-border bg-surface flex-shrink-0">
              {(['datasources', 'sql'] as LeftTab[]).map(t => (
                <button
                  key={t}
                  onClick={() => setLeftTab(t)}
                  className={cn(
                    'flex-1 py-2.5 text-[10px] font-bold tracking-widest uppercase transition-colors',
                    leftTab === t ? 'text-accent border-b-2 border-accent' : 'text-muted hover:text-ink'
                  )}
                >
                  {t === 'datasources' ? 'Sources' : 'SQL Editor'}
                </button>
              ))}
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => {
                  setSourcesPanelExpanded(p => !p)
                  if (!sourcesPanelExpanded) setEditorExpanded(false)
                }}
                className="px-3 text-muted hover:text-ink transition-colors"
                title={sourcesPanelExpanded ? 'Collapse' : 'Expand'}
              >
                {sourcesPanelExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </button>
            </div>

            {leftTab === 'datasources' && (
              <div className="flex flex-1 flex-col overflow-hidden">
                {/* Search */}
                <div className="px-3 pt-2.5 pb-2 flex-shrink-0">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted pointer-events-none" />
                    <input
                      className="w-full rounded border border-border2 bg-bg pl-6 pr-6 py-1 text-[11px] font-mono text-ink placeholder:text-muted focus:border-accent focus:outline-none transition-colors"
                      placeholder="Search sources…"
                      value={dsSearch}
                      onChange={e => setDsSearch(e.target.value)}
                    />
                    {dsSearch && (
                      <button onClick={() => setDsSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-ink">
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto px-3 pb-2 space-y-1.5">
                  {dsLoading ? (
                    <div className="flex justify-center py-6"><div className="h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent" /></div>
                  ) : allSources.length === 0 ? (
                    <p className="text-[10px] text-muted font-mono px-1 py-4 text-center">
                      {dsSearch ? `No sources match "${dsSearch}"` : 'No sources yet'}
                    </p>
                  ) : (
                    <>
                      <p className="text-[10px] text-muted font-mono px-1 pb-1">
                        Click to toggle · Drag to insert
                      </p>
                      {allSources.map(ds => {
                        const active = (template.requiredDataSourceKeys ?? []).includes(ds.key)
                        return (
                          <div
                            key={ds.id}
                            draggable
                            onDragStart={e => {
                              e.dataTransfer.setData(DS_DRAG_TYPE, JSON.stringify({ key: ds.key, isCollection: ds.isCollection }))
                              e.dataTransfer.effectAllowed = 'copy'
                            }}
                            onClick={() => toggleSource(ds.key)}
                            className={cn(
                              'flex items-center gap-2.5 rounded-md border px-3 py-2 cursor-grab active:cursor-grabbing transition-colors select-none',
                              active ? 'border-accent/40 bg-accent/5' : 'border-border hover:border-border2'
                            )}
                          >
                            <span className={cn('h-2 w-2 rounded-full flex-shrink-0', active ? 'bg-accent' : 'bg-border2')} />
                            <div className="flex-1 min-w-0">
                              <span className="text-[11px] font-mono text-green truncate block">{ds.key}</span>
                              <Badge variant={ds.isCollection ? 'accent' : 'green'} className="mt-0.5">
                                {ds.isCollection ? 'array' : 'obj'}
                              </Badge>
                            </div>
                            <button
                              onClick={e => { e.stopPropagation(); setEditingDs(ds); setLeftTab('sql') }}
                              className="text-muted hover:text-ink transition-colors flex-shrink-0"
                            >
                              <Database className="h-3 w-3" />
                            </button>
                          </div>
                        )
                      })}
                    </>
                  )}

                  <div className="border-t border-border mt-2 pt-2 px-1">
                    <SnippetPicker
                      type="Template"
                      onInsert={text => liquidEditorRef.current?.insertText(text)}
                    />
                  </div>
                </div>

                {/* Pager + New button */}
                <div className="border-t border-border flex-shrink-0">
                  {dsTotalPages > 1 && (
                    <div className="flex items-center justify-between px-3 py-1.5 border-b border-border">
                      <span className="text-[10px] font-mono text-muted">
                        {dsPage}/{dsTotalPages} · {dsTotalCount} total
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setDsPage(p => p - 1)}
                          disabled={dsPage === 1}
                          className="h-5 w-5 flex items-center justify-center rounded text-muted hover:text-ink disabled:opacity-30 transition-colors"
                        >
                          <ChevronLeft className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => setDsPage(p => p + 1)}
                          disabled={dsPage === dsTotalPages}
                          className="h-5 w-5 flex items-center justify-center rounded text-muted hover:text-ink disabled:opacity-30 transition-colors"
                        >
                          <ChevronRight className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="p-3">
                    <Button variant="ghost" className="w-full" size="sm" onClick={() => { setEditingDs(null); setLeftTab('sql') }}>
                      <Plus className="h-3 w-3" /> New Data Source
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {leftTab === 'sql' && (
              <div className="flex-1 overflow-hidden">
                <SqlEditor
                  dataSource={editingDs}
                  onSaved={async () => {
                    await loadSources()
                    setLeftTab('datasources')
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Right: Liquid editor + Excel layout */}
        {!sourcesPanelExpanded && (
        <div className="flex flex-1 flex-col overflow-hidden min-w-0">
          {/* Right panel tabs */}
          <div className="flex border-b border-border bg-surface flex-shrink-0">
            {(['liquid', 'excel'] as RightTab[]).map(t => (
              <button
                key={t}
                onClick={() => setRightTab(t)}
                className={cn(
                  'px-5 py-2.5 text-[10px] font-bold tracking-widest uppercase transition-colors',
                  rightTab === t ? 'text-accent border-b-2 border-accent' : 'text-muted hover:text-ink'
                )}
              >
                {t === 'liquid' ? 'Liquid + HTML' : 'Excel Layout'}
              </button>
            ))}
            <div className="flex-1" />
            {/* Expand/collapse only for liquid tab */}
            {rightTab === 'liquid' && (
              <button
                type="button"
                onClick={() => {
                  setEditorExpanded(p => !p)
                  if (!editorExpanded) setSourcesPanelExpanded(false)
                }}
                className="px-3 text-muted hover:text-ink transition-colors"
                title={editorExpanded ? 'Collapse' : 'Expand'}
              >
                {editorExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </button>
            )}
          </div>

          {rightTab === 'liquid' && (
            <div className="flex flex-1 overflow-hidden">
              {/* Monaco editor */}
              <div
                className={cn('flex flex-col overflow-hidden flex-1', !editorExpanded && 'border-r border-border')}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setIsDragOverEditor(true) }}
                onDragLeave={() => setIsDragOverEditor(false)}
                onDrop={handleEditorDrop}
              >
                {isDragOverEditor && (
                  <div className="border-b border-accent/30 bg-accent/5 px-4 py-1 text-[10px] text-accent font-mono animate-pulse flex-shrink-0">
                    Drop to insert snippet
                  </div>
                )}
                <div className={cn('flex-1 overflow-hidden', isDragOverEditor && 'ring-1 ring-inset ring-accent/40')}>
                  <LiquidEditor
                    ref={liquidEditorRef}
                    value={template.htmlContent ?? ''}
                    onChange={v => { setTemplate(p => ({ ...p, htmlContent: v })); schedulePreview() }}
                  />
                </div>
              </div>

              {/* Live preview */}
              {!editorExpanded && (
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="border-b border-border bg-surface px-4 py-2 flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] font-bold tracking-widest uppercase text-muted">Live Preview</span>
                    {previewing && <Spinner className="h-3 w-3" />}
                  </div>
                  <iframe ref={previewRef} className="flex-1 bg-white" title="preview" sandbox="allow-same-origin allow-scripts" />
                </div>
              )}
            </div>
          )}

          {rightTab === 'excel' && (
            <div className="flex-1 overflow-hidden">
              <ExcelLayoutEditor
                config={excelConfig}
                onChange={setExcelConfig}
                activeSources={activeSources}
                previewColumns={previewCols}
              />
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  )
}

const DEFAULT_HTML = `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: sans-serif; padding: 24px; color: #1a1a2e; }
    table { border-collapse: collapse; width: 100%; margin-top: 16px; }
    th, td { border: 1px solid #e0e0e0; padding: 8px 14px; text-align: left; }
    th { background: #f5f7fa; font-weight: 600; }
    caption { font-size: 20px; font-weight: 700; margin-bottom: 8px; text-align: left; }
  </style>
</head>
<body>
  <table>
    <caption>My Report</caption>
    <thead>
      <tr>
        <th>Name</th>
        <th>Value</th>
      </tr>
    </thead>
    <tbody>
      {% for r in collection.residents %}
      <tr>
        <td>{{ r.Name }}</td>
        <td>{{ r.Age }}</td>
      </tr>
      {% endfor %}
    </tbody>
  </table>
</body>
</html>`